/**
 * The share QR code, rendered locally.
 *
 * It has to be scannable across a table or a car window, so it is drawn crisply (integer module
 * size, no smoothing) with a quiet zone and a light background — dark-on-light is what phone
 * cameras expect, and an inverted code fails on many scanners.
 */

import qrcode from 'qrcode-generator';

export interface QrOptions {
  /** Rendered size in CSS pixels. */
  size?: number;
  /** Quiet zone in modules. The spec says four; anything less hurts scan rates. */
  quietZone?: number;
}

export function renderQrCanvas(text: string, options: QrOptions = {}): HTMLCanvasElement {
  const size = options.size ?? 240;
  const quietZone = options.quietZone ?? 4;

  // Type 0 = pick the smallest version that fits. 'M' error correction is the usual balance,
  // and share URLs are short enough that it costs nothing.
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();

  const moduleCount = qr.getModuleCount();
  const totalModules = moduleCount + quietZone * 2;

  // Snap to whole device pixels per module: a fractional module size produces blurred edges
  // that scanners struggle with in low light.
  const dpr = Math.min(window.devicePixelRatio || 1, 3);
  const modulePixels = Math.max(1, Math.floor((size * dpr) / totalModules));
  const canvasPixels = modulePixels * totalModules;

  const canvas = document.createElement('canvas');
  canvas.width = canvasPixels;
  canvas.height = canvasPixels;
  canvas.style.width = `${canvasPixels / dpr}px`;
  canvas.style.height = `${canvasPixels / dpr}px`;
  canvas.className = 'qr';
  canvas.setAttribute('role', 'img');
  canvas.setAttribute('aria-label', 'QR code for this session link');

  const context = canvas.getContext('2d');
  if (!context) return canvas;

  context.imageSmoothingEnabled = false;
  context.fillStyle = '#FFFFFF';
  context.fillRect(0, 0, canvasPixels, canvasPixels);

  context.fillStyle = '#0E1520';
  for (let row = 0; row < moduleCount; row++) {
    for (let column = 0; column < moduleCount; column++) {
      if (!qr.isDark(row, column)) continue;
      context.fillRect(
        (column + quietZone) * modulePixels,
        (row + quietZone) * modulePixels,
        modulePixels,
        modulePixels,
      );
    }
  }

  return canvas;
}
